/**
 * Business card scans (beta).
 *
 * Cards are photographed and read on the device (see
 * src/components/marketing/BusinessCardScannerSection.tsx); the browser posts
 * the photo plus the fields the scanner confirmed. Triage matches the other
 * marketing inboxes: tag a scan, then promote it into the client database.
 */

import { API_BASE } from '../config/apiBase';
import { duplicateClientError } from './clients';
import type { WebsiteFormLeadTag } from './websiteFormLeads';

export interface BusinessCardScan {
  id: number;
  image_file_name: string | null;
  /** Everything OCR read, kept so a bad parse can be fixed by hand. */
  raw_text: string | null;
  full_name: string | null;
  job_title: string | null;
  company: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  notes: string | null;
  scanned_by_user_id: number | null;
  scanned_by_username: string | null;
  converted_client_id: number | null;
  converted_company: string | null;
  converted_by_username: string | null;
  converted_at: string | null;
  tags: WebsiteFormLeadTag[];
  created_at: string | null;
  updated_at: string | null;
}

/** The editable contact fields, as shown in the confirm and edit forms. */
export interface BusinessCardFields {
  full_name: string;
  job_title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  notes: string;
}

export const emptyBusinessCardFields = (): BusinessCardFields => ({
  full_name: '',
  job_title: '',
  company: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  notes: '',
});

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw duplicateClientError(res.status, data) ?? new Error((data as { error?: string }).error || fallback);
  }
  return data as T;
}

export async function fetchBusinessCardScans(userId: number): Promise<BusinessCardScan[]> {
  const res = await fetch(
    `${API_BASE}/api/marketing/business-cards?userId=${encodeURIComponent(userId)}`
  );
  const data = await jsonOrThrow<{ scans: BusinessCardScan[] }>(res, 'Failed to fetch scans');
  return data.scans ?? [];
}

/** Saves a scan; `photo` is the captured card image when the camera was used. */
export async function createBusinessCardScan(
  userId: number,
  fields: BusinessCardFields,
  rawText: string,
  photo: Blob | null
): Promise<BusinessCardScan> {
  const form = new FormData();
  form.append('user_id', String(userId));
  form.append('raw_text', rawText);
  (Object.keys(fields) as (keyof BusinessCardFields)[]).forEach((key) => {
    form.append(key, fields[key] ?? '');
  });
  if (photo) form.append('image', photo, 'business-card.jpg');
  const res = await fetch(`${API_BASE}/api/marketing/business-cards`, {
    method: 'POST',
    body: form,
  });
  return jsonOrThrow(res, 'Failed to save the scan');
}

export async function updateBusinessCardScan(
  id: number,
  userId: number,
  fields: Partial<BusinessCardFields>
): Promise<BusinessCardScan> {
  const res = await fetch(`${API_BASE}/api/marketing/business-cards/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, ...fields }),
  });
  return jsonOrThrow(res, 'Failed to update the scan');
}

export async function deleteBusinessCardScan(id: number, userId: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/marketing/business-cards/${id}?user_id=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || 'Failed to delete the scan');
  }
}

/** Replaces the scan's whole tag list. */
export async function updateBusinessCardTags(
  id: number,
  userId: number,
  tags: WebsiteFormLeadTag[]
): Promise<BusinessCardScan> {
  const res = await fetch(`${API_BASE}/api/marketing/business-cards/${id}/tags`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      tags: tags.map((t) => ({ kind: t.kind, label: t.label })),
    }),
  });
  return jsonOrThrow(res, 'Failed to update tags');
}

/** Values shown in the confirmation modal, editable before the lead is created. */
export interface ConvertBusinessCardPayload {
  user_id: number;
  company: string;
  point_of_contact: string;
  contact_email?: string;
  contact_phone?: string;
  source?: string;
  industry?: string;
  notes?: string;
}

/** Adds the scan to the client database as a lead and emails the sales team. */
export async function convertBusinessCardScan(
  id: number,
  payload: ConvertBusinessCardPayload
): Promise<{ scan: BusinessCardScan; client_id: number }> {
  const res = await fetch(`${API_BASE}/api/marketing/business-cards/${id}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to add lead to the client database');
}

export function businessCardImageUrl(id: number, userId: number): string {
  return `${API_BASE}/api/marketing/business-cards/${id}/image?userId=${encodeURIComponent(userId)}`;
}
